import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

// prime react component
import { Toolbar } from "primereact/toolbar";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";

// data table option
import {
  bookingStatusDropdownItems,
  defaultPageRowOptions,
  defaultRowOptions,
  paginatorLinks,
  showingEntries,
  SkeletonbodyTemplate,
  Skeletonitems,
} from "../../../appconfig/Settings";

// farm table column
import {
  FarmerBookingListColumns,
  OperatorBookingListColumns,
} from "../../../appconfig/DataTableColumns";

// api services
import { getAllDetails } from "../../../service/ProductService";

// api constant
import { GET_BOOKING_LIST, GET_SERVICE_LIST } from "../../../config/ApiConstant";
import { Badge } from "primereact/badge";
import { amountTemplate } from "../../../appconfig/TemplateSetting";
import { FilterMatchMode } from "primereact/api";
import { globalNameFilterTemplate } from "../../../appconfig/AppHelper";
import { Dropdown } from "primereact/dropdown";

const OperatorBookingList = () => {
  document.title = "Operator Booking List | Agriown";

  // Get User ID From URL
  const { id }: any = useParams();
  // Decode User ID
  const operator_id = atob(id);

  // navigation
  const navigate = useNavigate();

  // /Set Toast/ Filter Properties
  const toast = useRef<Toast>(null);

  // Filter Properties
  const [globalFilter, setGlobalFilter] = useState<any>(null);
  const [statusFilterValue, setStatusFilterValue] = useState<any>(null);
  const [serviceFilterValue, setServiceFilterValue] = useState<any>(null);
  const [services, setServices] = useState<any[]>([]);

  //Loading/Page Loading
  const [pageLoad, setPageLoad] = useState<boolean>(false);
  const [operatorBookingList, setOperatorBookingList] = useState<any>([]);

  // datatable filter value sets
  const emptyFilters = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    farm_name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    operator_name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    service_name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    crop_name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    total_amount: { value: null, matchMode: FilterMatchMode.CONTAINS },
    booking_status: { value: null, matchMode: FilterMatchMode.EQUALS },
  };
  const [filters, setFilters] = useState(emptyFilters);

  // useEffect to get operator booking list on page load
  useEffect(() => {
    getOperatorBookingList();
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [operator_id]);

  // get services for filter
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await getAllDetails(GET_SERVICE_LIST);
        if (response) {
          setServices(response);
          setPageLoad(true);
        } else {
          setServices([]);
          setPageLoad(false);
        }
      } catch (error: any) {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: error?.response?.data?.message,
        });
        setPageLoad(false);
      }
    };
    fetchServices();
  }, []);

  // left toolbar
  const leftToolbarTemplate = () => {
    return (
      <span className="mr-3 search-bar">
        <i className="pi pi-search" />
        <InputText
          type="search"
          className="search-input"
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.currentTarget.value)}
          placeholder="Search..."
        />
      </span>
    );
  };

  //Clear Filters
  const rightToolbarTemplate = () => {
    return (
      <>
        <Button
          icon="pi pi-filter-slash"
          label="Clear"
          onClick={() => clearAll()}
          className="mr-2 p-button-outlined p-button-primary"
        ></Button>
      </>
    );
  };

  //Clear All Filter
  const clearAll = () => {
    setFilters({ ...emptyFilters });
    setGlobalFilter("");
    setStatusFilterValue(null);
    setServiceFilterValue(null)
  };

  // api call for get operator booking list
  const getOperatorBookingList = async () => {
    try {
      const response = await getAllDetails(
        `${GET_BOOKING_LIST}?operator_id=${operator_id}`
      );

      if (response) {
        setOperatorBookingList(response);
        setPageLoad(true);
      } else {
        setOperatorBookingList([]);
        setPageLoad(false);
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.response?.data?.message,
      });
      setPageLoad(false);
    }
  };

  // Status Template
  const statusTemplate = (rowData: any) => {
    // get booking status
    const status = !window.cn(rowData?.booking_status)
      ? rowData.booking_status
      : "-";

    // change badge color based on status
    const severityMap: Record<
      string,
      "success" | "warning" | "danger" | "info"
    > = {
      Confirmed: "info",
      Completed: "success",
      Pending: "warning",
      Cancelled: "danger",
    };

    return (
      <Badge
        severity={severityMap[status]}
        value={status}
        style={{ width: "fit-content" }}
      />
    );
  };

  //Dropdown Service Filter
  const serviceFilterTemplate = (options: any) => {
    return (
      <Dropdown
        filter
        value={serviceFilterValue}
        options={services}
        onChange={(e) => {
          setServiceFilterValue(e.value);
          options.filterCallback(e.value ? e.value : null, options.index);
        }}
        optionLabel="service_name"
        optionValue="service_name"
        placeholder="Select Service"
        className="order-drp"
        showClear
      />
    );
  };

  //Dropdown Status Filter
  const filterByStatus = (options: any) => {
    return (
      <Dropdown
        filter
        value={statusFilterValue}
        options={bookingStatusDropdownItems}
        onChange={(e) => {
          setStatusFilterValue(e.value);
          options.filterCallback(e.value ? e.value : null, options.index);
        }}
        optionLabel="name"
        optionValue="code"
        placeholder="Select Status"
        className="order-drp"
        showClear
      />
    );
  };

  // Operator Name Template
  const operatorNameTemplate = (rowData: any) => {
    return (
      <span>
        {!window.cn(rowData?.operator_name) ? rowData?.operator_name : "-"}
      </span>
    );
  };

  // action Template
  const actionBodyTemplate = (rowData: any) => (
    <div className="actions">
      <Button
        icon="pi pi-eye"
        className="mr-2 p-button-secondary"
        tooltip="View Detail"
        tooltipOptions={{ position: "top" }}
        onClick={() => navigate(`/booking/detail/${btoa(rowData.id)}`)}
      />
    </div>
  );

  // Time Slot Template
  const timeSlotTemplate = (rowData: any) => {
    const slot = rowData?.time_slot_data;

    if (window.cn(slot)) return "-";

    return (
      <span>
        {!window.cn(slot.slot) ? slot.slot : "-"} (
        {!window.cn(slot.start_time) ? slot.start_time : "-"} -{" "}
        {!window.cn(slot.end_time) ? slot.end_time : "-"})
      </span>
    );
  };

  return (
    <>
      <Toast ref={toast} />

      <div className="grid crud-demo">
        <div className="col-12">
          <div className="card">
            <Toolbar
              left={leftToolbarTemplate}
              right={rightToolbarTemplate}
            ></Toolbar>

            {pageLoad ? (
              <DataTable
                value={operatorBookingList}
                showGridlines
                stripedRows
                globalFilter={globalFilter || null}
                rows={defaultRowOptions}
                paginator={operatorBookingList.length >= 10}
                rowsPerPageOptions={defaultPageRowOptions}
                className="datatable-responsive p-datatable-sm"
                paginatorTemplate={paginatorLinks}
                currentPageReportTemplate={showingEntries}
                filters={{ ...filters, global: { ...filters.global, value: globalFilter || null } }}
                onFilter={(e: any) => setFilters(e.filters)}
                emptyMessage="No Data Found"
                removableSort
              >
                {OperatorBookingListColumns.map((col, i) => {
                  if (col.field === "action") {
                    return (
                      <Column
                        key={col.field}
                        field={col.field}
                        header={col.header}
                        body={actionBodyTemplate}
                      />
                    );
                  } else if (col.field === "booking_status") {
                    return (
                      <Column
                        key={col.field}
                        field={col.field}
                        header={col.header}
                        body={statusTemplate}
                        filter
                        filterElement={filterByStatus}
                      />
                    );
                  } else if (col.field === "farm_name") {
                    return (
                      <Column
                        key={col.field}
                        field={col.field}
                        header={col.header}
                      />
                    );
                  } else if (col.field === "farmer_name") {
                    return (
                      <Column
                        key={col.field}
                        field={col.field}
                        header={col.header}
                      />
                    );
                  } else if (col.field === "service_name") {
                    return (
                      <Column
                        key={col.field}
                        field={col.field}
                        header={col.header}
                        filter
                        filterElement={serviceFilterTemplate}
                      />
                    );
                  } else if (col.field === "crop_name") {
                    return (
                      <Column
                        key={col.field}
                        field={col.field}
                        header={col.header}
                        filter
                        filterElement={globalNameFilterTemplate}
                      />
                    );
                  } else if (col.field === "time_slot_data") {
                    return (
                      <Column
                        key={col.field}
                        field={col.field}
                        header={col.header}
                        sortable
                        body={timeSlotTemplate}
                      />
                    );
                  } else if (col.field === "total_amount") {
                    return (
                      <Column
                        key={col.field}
                        field={col.field}
                        header={col.header}
                        body={amountTemplate}
                        sortable
                      />
                    );
                  } else if (col.field === "operator_name") {
                    return (
                      <Column
                        key={col.field}
                        field={col.field}
                        header={col.header}
                        body={operatorNameTemplate}
                        filter
                        filterElement={globalNameFilterTemplate}
                      />
                    );
                  } else {
                    return (
                      <Column
                        key={col.field}
                        sortable
                        field={col.field}
                        header={col.header}
                      />
                    );
                  }
                })}
              </DataTable>
            ) : (
              <DataTable value={Skeletonitems}>
                {FarmerBookingListColumns.map((col: any) => (
                  <Column
                    key={col.field}
                    field={col.field}
                    header={col.header}
                    body={SkeletonbodyTemplate}
                  />
                ))}
              </DataTable>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default OperatorBookingList;
